import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Star, Clock, BookOpen, Zap, Building2, Heart } from 'lucide-react'
import { cn } from '../../app/libs/cn'
import type { ApiCourseListItem } from '../../types/course'
import { useAuthStore } from '@/store/authStore'
import { useFavoriteStore } from '@/store/favoriteStore'

const LEVEL_COLOR: Record<string, string> = {
  BEGINNER: 'text-emerald-400 bg-emerald-400/10',
  INTERMEDIATE: 'text-amber-400 bg-amber-400/10',
  ADVANCED: 'text-rose-400 bg-rose-400/10',
}
const LEVEL_LABEL: Record<string, string> = {
  BEGINNER: 'Beginner',
  INTERMEDIATE: 'Intermediate',
  ADVANCED: 'Advanced',
}

// ไม่มี column สีเก็บไว้ใน database (accent เป็นของตกแต่งล้วนๆ ไม่ใช่ข้อมูลธุรกิจ)
// เลยสุ่มไล่สีตาม category_id แทน ให้แต่ละหมวดหมู่ได้สีคงที่ ไม่เปลี่ยนไปมาทุกครั้งที่โหลดหน้า
const GRADIENTS = [
  'from-indigo-500 via-violet-500 to-purple-600',
  'from-cyan-400 via-sky-500 to-blue-600',
  'from-violet-400 via-purple-500 to-fuchsia-600',
  'from-amber-400 via-orange-500 to-yellow-600',
  'from-cyan-300 via-teal-400 to-cyan-600',
  'from-emerald-400 via-teal-500 to-green-600',
  'from-pink-400 via-rose-500 to-fuchsia-600',
  'from-yellow-400 via-amber-500 to-orange-600',
]
export const gradientForCategory = (category_id: number) => GRADIENTS[category_id % GRADIENTS.length]

export const formatDuration = (minutes: number) => {
  if (!minutes) return '-'
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h === 0) return `${m} นาที`
  if (m === 0) return `${h} ชม.`
  return `${h} ชม. ${m} นาที`
}

function CourseThumbnail({ course }: { course: ApiCourseListItem }) {
  const gradient = gradientForCategory(course.category_id)

  return (
    <div className={cn('relative h-44 w-full bg-gradient-to-br overflow-hidden rounded-t-xl', gradient)}>
      {course.thumbnail_url ? (
        <img src={course.thumbnail_url} alt={course.title} className="h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 opacity-20">
          <div className="h-16 w-16 rounded-2xl border-2 border-white/50" />
          <div className="h-2 w-24 rounded-full border border-white/50" />
          <div className="h-2 w-16 rounded-full border border-white/50" />
        </div>
      )}
      {/* Badges */}
      <div className="absolute left-3 top-3 flex gap-1.5">
        {course.is_bestseller && (
          <span className="rounded-md bg-amber-400 px-2 py-0.5 text-xs font-bold text-black">
            BESTSELLER
          </span>
        )}
        {course.is_free && (
          <span className="flex items-center gap-0.5 rounded-md bg-white px-2 py-0.5 text-xs font-bold text-bg">
            <Zap className="h-2.5 w-2.5" /> FREE
          </span>
        )}
        {course.course_format === 'ONSITE' && (
          <span className="flex items-center gap-1 rounded-md bg-amber-500 px-2 py-0.5 text-xs font-bold text-white shadow-sm">
            <Building2 className="h-3 w-3" /> หลักสูตรอบรม
          </span>
        )}
      </div>
    </div>
  )
}

export default function CourseCard({
  course,
  categoryName,
  index = 0,
}: {
  course: ApiCourseListItem
  categoryName?: string
  index?: number
}) {
  const navigate = useNavigate()
  const isLoggedIn = useAuthStore((s) => s.isLoggedIn)
  const isFavorited = useFavoriteStore((s) => s.favoriteIds.has(course.course_id))
  const toggleFavorite = useFavoriteStore((s) => s.toggleFavorite)

  const handleToggleFavorite = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!isLoggedIn) {
      navigate('/login')
      return
    }
    toggleFavorite(course.course_id)
  }

  // การ์ดทั้งใบเป็น Link ไปหน้าคอร์สอยู่แล้ว — แถวผู้สอนกดแยกไปหน้าผู้สอนได้โดยไม่โดน Link แม่จับ (nested <a> ผิด HTML)
  const handleInstructorClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(`/instructors/${course.instructor_id}`)
  }
  const instructorAvatar =
    course.instructor_avatar_url ||
    (course.instructor_name
      ? `https://api.dicebear.com/9.x/notionists/svg?seed=${encodeURIComponent(course.instructor_name)}`
      : '')

  // ราคาขั้นบันไดตามจำนวนคน (ไม่บังคับ) — ช่วงแรก (1 คน) แสดงเป็นราคาหลัก ช่วงถัดไปโชว์เป็น hint ส่วนลดกลุ่ม
  const tiers = course.price_tiers
  const firstTier = tiers && tiers.length > 0 ? tiers[0] : null
  const groupTier = tiers && tiers.length > 1 ? tiers[1] : null

  return (
    <motion.div
      className="h-full"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ delay: index * 0.08, duration: 0.5, ease: 'easeOut' as const }}
    >
      <Link to={`/courses/${course.slug}`} className="group flex h-full flex-col">
        <div className="card-flat card-flat-hover flex h-full flex-col overflow-hidden rounded-xl shadow-sm shadow-accent/5">
          <div className="relative shrink-0">
            <CourseThumbnail course={course} />
            <button
              onClick={handleToggleFavorite}
              className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/40 backdrop-blur-sm transition hover:bg-black/60"
              title={isFavorited ? 'เอาออกจากรายการโปรด' : 'บันทึกเป็นรายการโปรด'}
            >
              <Heart className={cn('h-4 w-4 transition-colors', isFavorited ? 'fill-rose-500 text-rose-500' : 'text-white')} />
            </button>
          </div>
          <div className="flex flex-1 flex-col p-5">
            {/* Category + Level */}
            <div className="mb-3 flex items-center justify-between">
              <span className="text-xs font-medium text-text-muted">{categoryName || '-'}</span>
              <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', LEVEL_COLOR[course.level])}>
                {LEVEL_LABEL[course.level]}
              </span>
            </div>

            {/* Title */}
            <h3 className="mb-2 line-clamp-2 text-sm font-semibold leading-snug text-text group-hover:text-accent transition-colors">
              {course.title}
            </h3>

            {course.short_description && (
              <p className="mb-3 line-clamp-2 text-xs text-text-muted">{course.short_description}</p>
            )}

            {/* กลุ่มล่าง (สถิติ + ผู้สอน + ราคา) — mt-auto ดันทั้งกลุ่มชิดขอบล่างการ์ด แถวผู้สอน/โปรโมชันจองความสูงไว้ทุกใบ
                ให้เส้นคั่นกับราคาอยู่ระดับเดียวกันทุกการ์ด แม้บางใบไม่มีผู้สอนหรือไม่มีส่วนลดกลุ่ม */}
            <div className="mt-auto">
            {/* Stats */}
            <div className="mb-3 flex items-center gap-3 text-xs text-text-muted">
              <span className="flex items-center gap-1">
                <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                <span className="font-semibold text-amber-400">{course.avg_rating.toFixed(1)}</span>
                <span>({course.review_count.toLocaleString()})</span>
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDuration(course.duration_minutes)}
              </span>
            </div>

            {/* ผู้สอน — กดดูโปรไฟล์ผู้สอนแยกได้ ไม่โดนการ์ดแม่พาไปหน้าคอร์สแทน */}
            {course.instructor_name && course.instructor_id ? (
              <button
                type="button"
                onClick={handleInstructorClick}
                className="mb-4 flex h-9 max-w-full items-center gap-2 py-1 text-left"
              >
                <span className="h-7 w-7 shrink-0 overflow-hidden rounded-full bg-surface2">
                  <img
                    src={instructorAvatar}
                    alt={course.instructor_name}
                    className="h-full w-full object-cover"
                  />
                </span>
                <span className="truncate text-xs font-medium text-text">{course.instructor_name}</span>
              </button>
            ) : (
              <div className="mb-4 h-9" aria-hidden="true" />
            )}

            {/* Price */}
            <div className="border-t border-border pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-baseline gap-2">
                  {course.is_free ? (
                    <span className="text-lg font-bold text-emerald-500">ฟรี!</span>
                  ) : (
                    <>
                      <span className="text-lg font-bold text-text">฿{firstTier ? firstTier.price.toLocaleString() : course.price.toLocaleString()}</span>
                      {(firstTier ? firstTier.original_price : course.original_price) && (
                        <span className="text-xs text-text-muted line-through">
                          ฿{(firstTier ? firstTier.original_price : course.original_price)!.toLocaleString()}
                        </span>
                      )}
                      {firstTier && <span className="text-xs text-text-muted">/คน</span>}
                    </>
                  )}
                </div>
                <div className="flex items-center gap-1 text-xs text-text-muted">
                  <BookOpen className="h-3 w-3" />
                  {course.lesson_count} บท
                </div>
              </div>
              {/* จองความสูงบรรทัดโปรโมชันไว้ทุกใบ (บางใบไม่มีราคาขั้นบันได) ราคาจะได้ไม่ขยับขึ้นลง */}
              <p className="mt-1.5 h-4 text-[11px] font-medium leading-4 text-accent">
                {groupTier && `🔥 สมัคร ${groupTier.min_qty} ท่านขึ้นไป เหลือ ฿${groupTier.price.toLocaleString()}/ท่าน`}
              </p>
            </div>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  )
}
